<template>
  <div class="ops-wall" :class="`ops-wall--${themeName}`" :style="vars" :data-wall-theme="themeName" data-test="ops-wall">
    <!-- The canvas is the design's 1920 x 1080 frame, scaled to this screen (see `canvasStyle`): the whole Wall always fits, no page scroll. -->
    <div class="ops-wall__canvas" :style="canvasStyle" data-test="ops-wall-canvas">
      <header class="ops-wall__head">
        <span class="ops-wall__logo" aria-hidden="true"></span>
        <div class="ops-wall__titles">
          <div class="ops-wall__title" data-test="ops-wall-title">Practice Ops</div>
          <div class="ops-wall__sub" data-test="ops-wall-sub">{{ subtitle }}</div>
        </div>
        <div class="ops-wall__clock" data-test="ops-wall-clock">{{ clock }}<span class="ops-wall__meridiem">{{ meridiem }}</span></div>
      </header>

      <div class="ops-wall__grid">
        <!-- Check-in -->
        <section class="ops-wall__box ops-wall__checkin" aria-label="Check-in" data-test="ops-wall-checkin">
          <div class="ops-wall__boxhead">
            <div class="ops-wall__boxtitle">Check-in</div>
            <div class="ops-wall__boxsub" data-test="ops-wall-checkin-sub">{{ arrivedReady }}</div>
            <div v-if="waiting.a" class="ops-wall__boxwarn" data-test="ops-wall-checkin-warn">{{ waiting.a }}<span class="ops-wall__amber">{{ waiting.b }}</span></div>
          </div>
          <OpsWallClip v-if="board.checkin.length" :items="board.checkin">
            <template #default="{ item }"><OpsPatientCard v-bind="cardProps(item, 'xl')" /></template>
          </OpsWallClip>
          <div v-else class="ops-wall__empty" data-test="ops-wall-checkin-empty">No one waiting</div>
        </section>

        <!-- Rooms: a 3-column grid whose rows share the height -->
        <section class="ops-wall__rooms" :style="roomsStyle" aria-label="Rooms" data-test="ops-wall-rooms">
          <div v-for="room in board.rooms" :key="room.opNum" class="ops-wall__box ops-wall__room" :class="{ 'ops-wall__room--dense': roomRows > 3 }" :data-test="`ops-wall-room-${room.opNum}`">
            <div class="ops-wall__roomhead">
              <span class="ops-wall__roomname">{{ room.label }}</span>
              <span class="ops-wall__roomprov">{{ roomProvider(room) }}</span>
            </div>
            <OpsWallClip v-if="room.cards.length" :items="room.cards">
              <template #default="{ item }"><OpsPatientCard v-bind="cardProps(item, roomCardSize)" /></template>
            </OpsWallClip>
            <div v-else class="ops-wall__open" data-test="ops-wall-open">
              <span class="ops-wall__opentitle">Room open</span>
              <span v-if="leftText(room)" class="ops-wall__openleft">{{ leftText(room) }}</span>
            </div>
          </div>
          <div v-if="!board.rooms.length && !skeleton" class="ops-wall__empty ops-wall__norooms" data-test="ops-wall-no-rooms">No rooms are set up yet.</div>
        </section>

        <!-- Check-out and Alerts -->
        <div class="ops-wall__side">
          <section class="ops-wall__box ops-wall__checkout" aria-label="Check-out" data-test="ops-wall-checkout">
            <div class="ops-wall__boxhead">
              <div class="ops-wall__boxtitle">Check-out</div>
              <div class="ops-wall__boxsub">{{ checkoutSub }}</div>
            </div>
            <OpsWallClip v-if="board.checkout.length" :items="board.checkout">
              <template #default="{ item }"><OpsPatientCard v-bind="cardProps(item, 'lg')" :interactive="false" /></template>
            </OpsWallClip>
            <div v-else class="ops-wall__empty">No one at check-out</div>
          </section>

          <!-- Alerts: one compact stripe per ping — the recipient's colour on the edge, the room, the time and (when it fits) the ask. -->
          <section class="ops-wall__box ops-wall__alerts" aria-label="Alerts" data-test="ops-wall-alerts">
            <div class="ops-wall__boxtitle">Alerts</div>
            <OpsWallClip v-if="pings.pings.length" :items="pings.pings">
              <template #default="{ item }">
                <div class="ops-wall__ping" :style="pingStyle(item)" data-test="ops-wall-ping">
                  <span class="ops-wall__pingedge" :style="{ background: pingEdge(item) }" aria-hidden="true"></span>
                  <span class="ops-wall__pingop">{{ item.op }}</span>
                  <span class="ops-wall__pingat" :style="pingAtStyle(item)">{{ item.at }}</span>
                  <span class="ops-wall__pingmsg">{{ item.msg }}</span>
                </div>
              </template>
            </OpsWallClip>
            <div v-else class="ops-wall__empty" data-test="ops-wall-alerts-empty">No alerts</div>
          </section>
        </div>
      </div>

      <!-- The legend: what the dots, the borders and the icons mean. -->
      <footer class="ops-wall__legend" data-test="ops-wall-legend">
        <div class="ops-wall__legendrow">
          <span class="ops-wall__legendlabel">STATUS</span>
          <span v-for="s in statusLegend" :key="s.name" class="ops-wall__legenditem" :data-test="`ops-wall-status-${s.id}`">
            <span class="ops-wall__dot" :style="{ background: s.color }"></span>{{ s.name }}
          </span>
          <template v-if="board.thresholdMin">
            <span class="ops-wall__legendlabel ops-wall__legendlabel--gap">WAIT</span>
            <span class="ops-wall__legenditem" data-test="ops-wall-wait-amber"><span class="ops-wall__swatch ops-wall__swatch--amber"></span>Over {{ board.thresholdMin }} min</span>
            <span class="ops-wall__legenditem" data-test="ops-wall-wait-red"><span class="ops-wall__swatch ops-wall__swatch--red"></span>Over {{ board.thresholdMin * 2 }} min</span>
          </template>
        </div>
        <div v-if="alertLegend.length" class="ops-wall__alertlegend" data-test="ops-wall-alert-legend">
          <span v-for="a in alertLegend" :key="a.key" class="ops-wall__alertitem" :data-test="`ops-wall-legend-${a.key}`">
            <span class="ops-wall__lchip"><AlertBadge :alert-key="a.key" /></span>{{ a.label }}
          </span>
        </div>
      </footer>
    </div>

    <!-- Appears on a pointer move, a tap or a key, hides after 3 s: the TV keeps its "no buttons" look. -->
    <div v-if="cornerVisible" class="ops-wall__corner" data-test="ops-wall-corner">
      <button type="button" class="ops-wall__cbtn ops-wall__cbtn--text" data-test="ops-wall-exit" @click="$emit('exit')">Exit wall display</button>
      <button
        type="button"
        class="ops-wall__cbtn ops-wall__cbtn--icon"
        :aria-label="`Switch to ${otherTheme} theme`"
        :title="`Switch to ${otherTheme} theme`"
        data-test="ops-wall-theme-toggle"
        @click="$emit('toggle-theme')"
      >
        <svg v-if="themeName === 'dark'" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" data-test="ops-wall-icon-sun"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>
        <svg v-else viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" data-test="ops-wall-icon-moon"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * The Practice Ops Wall display (plan "Design review" → Wall display): the board on a TV. Read from three metres.
 *
 *   NO drag, NO buttons, NO chat, NO appointment note (it can be clinical and passers-by see the TV). The one exception is the
 *   corner group (the Light / Dark switch and "Exit wall display"): it is NOT in the DOM until a pointer move, a tap or a key,
 *   and it is removed again 3 s later, so an idle TV shows no control at all.
 *
 * The cards are OpsPatientCard at `xl` (time top-right, the alert icons on the timer row, no bell / menu / note) — Check-in and
 * the rooms — and, as in the design, `lg` for the Check-out list, both read-only (`interactive` false: the icons are spans).
 * Rooms are a 3-column grid; a large clock sits top right; the legend at the bottom shows the STATUS dots, the WAIT borders and
 * the alert icons (utils/scheduleView ALERT_META — the same icons AlertBadge draws on the cards).
 *
 * IT ALWAYS FITS THE SCREEN, with no page scroll. Two things do that:
 *   1. SCALE. The Wall is laid out as the design's 1920 x 1080 frame (every size below is that frame's px) and scaled with CSS `zoom`
 *      to the window (viewPref.js wallZoom): a 1280 x 720 laptop gets 2/3 of it, a 4K TV 2x. A window of another shape gets MORE room
 *      in one direction, never less, so the canvas is always the window's exact size.
 *   2. CLIP. Check-in, Check-out, each room and Alerts are OpsWallClip columns: they fill the height they are given, draw what fits
 *      and say "+N more" for the rest. The rooms' rows share the height (`minmax(0, 1fr)`); many rooms get a smaller card.
 *
 * THEMES. ONE `theme` prop ('light' | 'dark') feeds both this file's own tokens (WALL_THEMES, as CSS custom properties on the root)
 * and every card's `dark` prop; there is no colour here that is not a token, and every text rule sets its own `color` from one. The
 * corner switch emits `toggle-theme`; the PAGE flips the theme live and stores it (viewPref.js writeWallTheme). `?theme=` is read by
 * the page (viewPref.js wallTheme).
 *
 * ⛔ The root carries `data-wall-theme`, NEVER `data-theme`: src/css/copilotkit/input.css sets `color: … !important` on every
 * `[data-theme="dark"]` (the agent sidebar's dark skin), which turned every dark Wall heading dark-on-dark.
 *
 *   emits  toggle-theme · exit
 *
 * ⛔ No prop is named `patNum`, `siteID`, `loading` or `allSettings` (mixin.js computeds shadow them).
 */
import OpsPatientCard from './OpsPatientCard.vue';
import OpsWallClip from './OpsWallClip.vue';
import AlertBadge from '@/pages/ScheduleView/components/AlertBadge.vue';
import { ALERT_META } from '@/utils/scheduleView/settingsForm';
import { isTooLight } from '@/utils/scheduleView/provColor';
import { alertSwitchOn } from '@/utils/practiceOps/viewSettings';
import { WALL_THEMES, wallZoom } from '@/utils/practiceOps/viewPref';
import {
  clockText, meridiemText, arrivedReadyLine, checkoutLine, waitingLine, tintHex,
} from '@/utils/practiceOps/boardText';

/** The corner group stays this long after the last move / tap / key. */
const CORNER_HIDE_MS = 3000;

const ROOM_COLUMNS = 3;

const STATUS_LEGEND = [
  { id: 'arrived', name: 'Arrived', token: 'dotArrived' },
  { id: 'ready', name: 'Ready', token: 'dotReady' },
  { id: 'inTreatment', name: 'In treatment', token: 'dotInTreatment' },
  { id: 'dismissed', name: 'Dismissed', token: 'dotDismissed' },
  { id: 'unconfirmed', name: 'Unconfirmed', token: 'dotUnconfirmed' },
];

const WAKE_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown'];

export default {
  name: 'OpsWall',

  components: { OpsPatientCard, OpsWallClip, AlertBadge },

  props: {
    /** board.js buildBoard() */
    board: { type: Object, required: true },
    /** { pings, legend, hasLegend } — boardText.js buildPings() */
    pings: { type: Object, default: () => ({ pings: [], legend: [], hasLegend: false }) },
    /** 'light' | 'dark' */
    theme: { type: String, default: 'light' },
    siteName: { type: String, default: '' },
    /** "Friday, October 30" */
    dateText: { type: String, default: '' },
    zone: { type: String, default: '' },
    nowMs: { type: Number, default: 0 },
    alertPayload: { type: Object, default: null },
    viewSettings: { type: Object, default: null },
    showAlertIcons: { type: Boolean, default: true },
    skeleton: { type: Boolean, default: false },
    stripeFor: { type: Function, default: () => '' },
    /** card → { proc, note }. Only `proc` is used: the note never reaches the Wall. */
    textFor: { type: Function, default: card => ({ proc: card.procDescript || '', note: '' }) },
    birthdayFor: { type: Function, default: () => false },
    providerFor: { type: Function, default: () => '' },
  },

  data() {
    return {
      cornerVisible: false,
      /** The window, in CSS px — the canvas is scaled to it. */
      viewW: typeof window !== 'undefined' ? window.innerWidth : 0,
      viewH: typeof window !== 'undefined' ? window.innerHeight : 0,
    };
  },

  computed: {
    themeName() {
      return this.theme === 'dark' ? 'dark' : 'light';
    },

    otherTheme() {
      return this.themeName === 'dark' ? 'light' : 'dark';
    },

    tokens() {
      return WALL_THEMES[this.themeName];
    },

    /** Every colour of the Wall, once, as custom properties. */
    vars() {
      const t = this.tokens;
      return {
        '--w-bg': t.pageBg,
        '--w-col': t.columnBg,
        '--w-surf': t.cardBg,
        '--w-bd': t.border,
        '--w-ink': t.text,
        '--w-sub': t.textSub,
        '--w-muted': t.textMuted,
        '--w-accent': t.accent,
        '--w-amber': t.amberBorder,
        '--w-red': t.redBorder,
        '--w-chip': t.chipNeutralBg,
      };
    },

    /**
     * The 1920 x 1080 frame scaled to the window: the canvas is (window / zoom) CSS px, zoomed by `zoom`, so it covers the window
     * exactly. No usable window size (a test DOM) ⇒ no scaling at all.
     */
    canvasStyle() {
      if (!(this.viewW > 0) || !(this.viewH > 0)) return {};
      const zoom = wallZoom(this.viewW, this.viewH);
      return {
        zoom: zoom.toFixed(4),
        width: `${Math.ceil(this.viewW / zoom)}px`,
        height: `${Math.ceil(this.viewH / zoom)}px`,
      };
    },

    subtitle() {
      return [this.siteName, this.dateText].filter(Boolean).join(' · ');
    },

    clock() {
      return clockText(this.nowMs, this.zone);
    },

    meridiem() {
      return meridiemText(this.nowMs, this.zone);
    },

    arrivedReady() {
      return arrivedReadyLine(this.board.counts);
    },

    waiting() {
      return waitingLine(this.board.counts, this.board.thresholdMin);
    },

    checkoutSub() {
      return checkoutLine(this.board.counts);
    },

    statusLegend() {
      return STATUS_LEGEND.map(s => ({ id: s.id, name: s.name, color: this.tokens[s.token] }));
    },

    /** The alert icons, in ALERT_META's order — only those the office has switched on, and none when the office cannot have icons. */
    alertLegend() {
      if (!this.showAlertIcons) return [];
      return ALERT_META.filter(a => alertSwitchOn(this.viewSettings, a.key)).map(a => ({ key: a.key, label: a.label }));
    },

    /** The room rows share the height: as many equal rows as the rooms need at three across. */
    roomRows() {
      return Math.max(1, Math.ceil(this.board.rooms.length / ROOM_COLUMNS));
    },

    roomsStyle() {
      return { gridTemplateRows: `repeat(${this.roomRows}, minmax(0, 1fr))` };
    },

    /** The card of a room tile: 1-2 rows of rooms `xl`, 3 rows `lg`, 4-5 `md`, more `sm` — what fits a tile of that height. */
    roomCardSize() {
      if (this.roomRows <= 2) return 'xl';
      if (this.roomRows === 3) return 'lg';
      return this.roomRows <= 5 ? 'md' : 'sm';
    },
  },

  mounted() {
    this.hideTimer = null;
    WAKE_EVENTS.forEach((name) => { window.addEventListener(name, this.wake, { passive: true }); });
    window.addEventListener('resize', this.onResize, { passive: true });
  },

  beforeDestroy() {
    WAKE_EVENTS.forEach((name) => { window.removeEventListener(name, this.wake); });
    window.removeEventListener('resize', this.onResize);
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = null;
  },

  methods: {
    /** The window changed size (a TV switching input, a laptop window dragged): rescale. */
    onResize() {
      this.viewW = window.innerWidth;
      this.viewH = window.innerHeight;
    },

    /** Show the corner group and (re)start its 3 s countdown. */
    wake() {
      this.cornerVisible = true;
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.hideTimer = null;
        this.cornerVisible = false;
      }, CORNER_HIDE_MS);
    },

    /** Everything one OpsPatientCard is given. The note is never passed: the Wall is read by passers-by. */
    cardProps(card, size) {
      const text = this.textFor(card) || {};
      return {
        card,
        size,
        dark: this.themeName === 'dark',
        stripe: this.stripeFor(card) || '',
        proc: text.proc || '',
        note: '',
        zone: this.zone,
        alertPayload: this.alertPayload,
        viewSettings: this.viewSettings,
        showAlertIcons: this.showAlertIcons,
        birthday: !!this.birthdayFor(card),
        interactive: false,
      };
    },

    roomProvider(room) {
      const first = room.cards[0];
      return first ? this.providerFor(first) : '';
    },

    leftText(room) {
      if (typeof room.lastLeftMs !== 'number' || this.nowMs < room.lastLeftMs) return '';
      return `Last patient left ${Math.floor((this.nowMs - room.lastLeftMs) / 60000)} min ago`;
    },

    // ── the Alerts column ──
    pingColor(ping) {
      const c = ping && ping.color;
      return typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c) ? c : null;
    },

    pingEdge(ping) {
      const c = this.pingColor(ping);
      return c && !isTooLight(c) ? c : this.tokens.stripeFallback;
    },

    /** The recipient's colour tints the stripe (dark-tinted in the dark theme); a ping under a minute old has the accent border. */
    pingStyle(ping) {
      const c = this.pingColor(ping);
      const fresh = ping.elapsed && typeof ping.elapsed.minutes === 'number' && ping.elapsed.minutes < 1;
      return {
        background: c ? tintHex(c, this.themeName === 'dark' ? 0.22 : 0.2, this.tokens.cardBg) : this.tokens.cardBg,
        borderColor: fresh ? this.tokens.accent : this.tokens.border,
      };
    },

    /** The ping's time takes the wait tone (amber past the office's limit, red past twice it) — the stripe has no elapsed chip. */
    pingAtStyle(ping) {
      const t = this.tokens;
      const tone = ping.elapsed ? ping.elapsed.tone : 'n';
      if (tone === 'r') return { color: t.chipRedText };
      if (tone === 'a') return { color: t.chipAmberText };
      return {};
    },
  },
};
</script>

<style lang="scss" scoped>
// Every colour is a custom property set from WALL_THEMES on the root (`vars`), and every text rule below sets its own `color` from one
// (never rely on inheriting: a global rule on an ancestor must not be able to repaint a heading). Sizes are the design's 1920 x 1080
// frame, in px — the app root is 14px, so rem would shrink them — and the canvas is zoomed to the screen.
.ops-wall {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: var(--w-bg);
  color: var(--w-ink);
  font-family: inherit;
}

.ops-wall__canvas {
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  overflow: hidden;
  padding: 24px 40px 20px;
}

.ops-wall__head {
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 84px;
  flex: none;
}

.ops-wall__logo {
  flex: none;
  width: 48px;
  height: 48px;
  border-radius: 13px;
  background: var(--w-accent);
}

.ops-wall__title {
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--w-ink);
}

.ops-wall__sub {
  margin-top: 6px;
  font-size: 22px;
  color: var(--w-muted);
}

.ops-wall__clock {
  margin-left: auto;
  font-size: 80px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--w-ink);
}

.ops-wall__meridiem {
  margin-left: 8px;
  font-size: 32px;
  color: var(--w-muted);
}

// ── the three areas (one row that takes the rest of the height) ──
.ops-wall__grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 420px minmax(0, 1fr) 390px;
  grid-template-rows: minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;
  min-width: 0;
}

.ops-wall__box {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  padding: 18px;
  border: 1px solid var(--w-bd);
  border-radius: 18px;
  background: var(--w-col);
  color: var(--w-ink);
}

.ops-wall__boxhead { flex: none; }

.ops-wall__boxtitle {
  flex: none;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: var(--w-ink);
}

.ops-wall__boxsub {
  margin-top: 2px;
  font-size: 22px;
  color: var(--w-muted);
}

.ops-wall__boxwarn {
  margin-top: 2px;
  font-size: 22px;
  font-weight: 700;
  color: var(--w-ink);
}

.ops-wall__amber { color: var(--w-amber); }

.ops-wall__empty {
  flex: none;
  padding: 18px 12px;
  border: 2px dashed var(--w-bd);
  border-radius: 14px;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: var(--w-muted);
}

// rooms: three across, the rows (set inline from the room count) share the height
.ops-wall__rooms {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  min-width: 0;
  min-height: 0;
}

.ops-wall__norooms { grid-column: 1 / -1; }

.ops-wall__room {
  gap: 8px;
  padding: 12px 16px;
}

.ops-wall__roomhead {
  flex: none;
  display: flex;
  align-items: baseline;
  gap: 10px;
}

// the room's name keeps its width; the provider takes what is left (and gives way first)
.ops-wall__roomname {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.2;
  color: var(--w-ink);
}

.ops-wall__roomprov {
  flex: 1 1 0;
  min-width: 56px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: right;
  font-size: 20px;
  color: var(--w-muted);
}

// many rooms: tighter tiles
.ops-wall__room--dense {
  gap: 6px;
  padding: 10px 12px;
  border-radius: 14px;

  .ops-wall__roomname { font-size: 20px; }
  .ops-wall__roomprov { font-size: 16px; }
}

.ops-wall__open {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 2px dashed var(--w-bd);
  border-radius: 14px;
}

.ops-wall__opentitle {
  font-size: 30px;
  font-weight: 800;
  color: var(--w-muted);
}

.ops-wall__openleft {
  font-size: 20px;
  color: var(--w-muted);
}

.ops-wall__room--dense .ops-wall__opentitle { font-size: 22px; }
.ops-wall__room--dense .ops-wall__openleft { font-size: 16px; }

// Check-out on top, Alerts below: equal halves, as in the design
.ops-wall__side {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  min-height: 0;
}

.ops-wall__checkout,
.ops-wall__alerts { flex: 1 1 0; }

// ── a ping: a compact stripe ──
.ops-wall__ping {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 10px;
  align-items: baseline;
  overflow: hidden;
  padding: 8px 14px 8px 22px;
  border: 1.5px solid var(--w-bd);
  border-radius: 12px;
  color: var(--w-ink);
}

.ops-wall__pingedge {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 8px;
}

.ops-wall__pingop {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  color: var(--w-ink);
}

.ops-wall__pingat {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--w-muted);
  font-variant-numeric: tabular-nums;
}

// the ask, on one line: it shows what fits
.ops-wall__pingmsg {
  grid-column: 1 / -1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--w-sub);
}

// ── the legend ──
.ops-wall__legend {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--w-bd);
  color: var(--w-ink);
}

.ops-wall__legendrow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 26px;
  font-size: 18px;
  color: var(--w-ink);
}

.ops-wall__legendlabel {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: var(--w-muted);
}

.ops-wall__legendlabel--gap { margin-left: 16px; }

.ops-wall__legenditem {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ops-wall__dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
}

.ops-wall__swatch {
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 2.5px solid;
}

.ops-wall__swatch--amber { border-color: var(--w-amber); }
.ops-wall__swatch--red { border-color: var(--w-red); }

.ops-wall__alertlegend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
  font-size: 17px;
  color: var(--w-sub);
}

.ops-wall__alertitem {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.ops-wall__lchip {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: var(--w-chip);
  color: var(--w-sub);

  ::v-deep .sv-badge {
    width: 100%;
    height: 100%;
    background: transparent;
    color: inherit;
    border-radius: inherit;
    font-size: 17px;
  }
}

// ── the corner group (only while awake; NOT scaled: it is a control, not the picture) ──
.ops-wall__corner {
  position: fixed;
  top: 10px;
  right: 14px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ops-wall__cbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border: 1px solid var(--w-bd);
  border-radius: 10px;
  background: var(--w-surf);
  color: var(--w-ink);
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover { border-color: var(--w-accent); }
  svg { display: block; }
}

.ops-wall__cbtn--text { padding: 0 14px; }
.ops-wall__cbtn--icon { width: 40px; padding: 0; }
</style>
